import { useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';
import { dressProducts } from '@/mocks/products';

function RevealDiv({ children, className, style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add('ew-revealed'); obs.unobserve(el); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <div ref={ref} className={`ew-reveal ${className ?? ''}`} style={style}>{children}</div>;
}

const wearWays = [
  { num: '01', title: 'Classic Goddess Gown', desc: 'Wear it long and flowing for a full-length goddess look — perfect for events, festivals, or any day you want to feel magical.' },
  { num: '02', title: 'Wrap Dress', desc: 'Wrap and tie the fabric to create a fitted silhouette that flatters every body type.' },
  { num: '03', title: 'Off-Shoulder Drape', desc: 'Let one shoulder breathe for a relaxed, bohemian off-shoulder style.' },
  { num: '04', title: 'Tunic & Pants', desc: 'Pair it over leggings or wide-leg pants for a chic layered look.' },
  { num: '05', title: 'Halter Style', desc: 'Tie it at the neck for a halter silhouette that shows off your shoulders.' },
  { num: '06', title: 'Skirt Mode', desc: 'Wrap it around your waist as a flowing maxi skirt over your favorite top.' },
];

const dresses = dressProducts;

export default function MagicDresses() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-20">
        {/* Hero */}
        <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img src="https://public.readdy.ai/ai/img_res/e7bec8ce9eb13820bdd7c4858e319c87.jpg" alt="Magic Dress hero" className="w-full h-full object-cover object-top" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/20"></div>
          </div>
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 text-left">
              <p style={{ fontFamily: '"Dancing Script", cursive', color: '#E8B96A', fontSize: '1.4rem' }} className="mb-3">Our Signature Piece</p>
              <h1 style={{ fontFamily: '"Playfair Display", serif', color: '#fff', lineHeight: 1.1 }} className="text-5xl md:text-7xl font-black mb-4">
                The<br /><em style={{ color: '#E8B96A' }}>Magic Dress</em>
              </h1>
              <p style={{ fontFamily: '"Lato", sans-serif', color: '#EDE5D8', lineHeight: 1.8 }} className="text-lg mb-8 max-w-lg">
                Moroccan-inspired. Wearable 10+ ways. Beloved by customers for decades. One dress, infinite possibilities — for every body, every occasion, every mood.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#dress-collection" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5" style={{ backgroundColor: '#C9933A', color: '#fff', fontFamily: '"Lato", sans-serif' }}>
                  See the Magic Dress
                </a>
                <Link to="/magic-dress-howtos" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5 border-2 border-white" style={{ color: '#fff', fontFamily: '"Lato", sans-serif', backgroundColor: 'transparent' }}>
                  How to Wear It
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Ways to wear */}
        <section className="py-20" style={{ backgroundColor: '#1E4F4F' }}>
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <RevealDiv className="text-center mb-14">
              <p style={{ fontFamily: '"Dancing Script", cursive', color: '#E8B96A', fontSize: '1.2rem' }}>One dress. Endless possibilities.</p>
              <h2 style={{ fontFamily: '"Playfair Display", serif', color: '#FAF7F2' }} className="text-4xl font-bold mt-1">6 Ways to Wear the Magic Dress</h2>
            </RevealDiv>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {wearWays.map((w, i) => (
                <RevealDiv key={w.num} style={{ transitionDelay: `${i * 0.08}s` }}>
                  <div className="p-7 rounded-2xl h-full" style={{ backgroundColor: '#2C6B6B' }}>
                    <p style={{ fontFamily: '"Playfair Display", serif', color: '#E8B96A', fontSize: '2.5rem', fontWeight: 900, opacity: 0.4, lineHeight: 1 }}>{w.num}</p>
                    <h3 style={{ fontFamily: '"Playfair Display", serif', color: '#FAF7F2' }} className="text-xl font-bold mt-1 mb-3">{w.title}</h3>
                    <p style={{ fontFamily: '"Lato", sans-serif', color: '#A0C8C8', lineHeight: 1.7 }} className="text-sm">{w.desc}</p>
                  </div>
                </RevealDiv>
              ))}
            </div>
            <RevealDiv className="text-center mt-10">
              <Link to="/magic-dress-howtos" className="inline-flex items-center gap-2 px-8 py-4 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5" style={{ backgroundColor: '#C9933A', color: '#fff', fontFamily: '"Lato", sans-serif' }}>
                See Full How-To Guide <i className="ri-arrow-right-line"></i>
              </Link>
            </RevealDiv>
          </div>
        </section>

        {/* Collection */}
        <section id="dress-collection" className="py-20" style={{ backgroundColor: '#FAF7F2' }}>
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <RevealDiv className="text-center mb-12">
              <p style={{ fontFamily: '"Dancing Script", cursive', color: '#C9933A', fontSize: '1.2rem' }}>Available in multiple styles & colors</p>
              <h2 style={{ fontFamily: '"Playfair Display", serif', color: '#4A3728' }} className="text-4xl font-bold mt-1">The Magic Dress Collection</h2>
            </RevealDiv>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
              {dresses.map((d, i) => (
                <RevealDiv key={d.id} style={{ transitionDelay: `${i * 0.08}s` }}>
                  <div className="rounded-2xl overflow-hidden group cursor-pointer" style={{ backgroundColor: '#fff' }}>
                    <div className="overflow-hidden" style={{ height: '320px' }}>
                      <img src={d.img} alt={d.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="p-5">
                      <h3 style={{ fontFamily: '"Playfair Display", serif', color: '#4A3728' }} className="text-lg font-bold mb-1">{d.title}</h3>
                      <p style={{ fontFamily: '"Lato", sans-serif', color: '#C9933A', fontWeight: 700 }} className="text-sm mb-1">{d.price}</p>
                      <p style={{ fontFamily: '"Lato", sans-serif', color: '#7A5C42' }} className="text-xs mb-3">{d.colors}</p>
                      <a href="mailto:earthwisdom80@gmail.com" className="inline-flex items-center gap-1.5 text-sm font-bold whitespace-nowrap" style={{ color: '#C9933A', fontFamily: '"Lato", sans-serif' }}>
                        Inquire to Order <i className="ri-arrow-right-line"></i>
                      </a>
                    </div>
                  </div>
                </RevealDiv>
              ))}
            </div>
          </div>
        </section>

        {/* Plus-size note */}
        <RevealDiv>
          <section className="py-16" style={{ background: 'linear-gradient(135deg, #C9933A 0%, #7A2E2E 100%)' }}>
            <div className="max-w-3xl mx-auto px-4 text-center">
              <i className="ri-heart-3-line text-4xl mb-4 block" style={{ color: '#fff' }}></i>
              <h2 style={{ fontFamily: '"Playfair Display", serif', color: '#fff' }} className="text-3xl font-bold mb-4">Made for Every Goddess</h2>
              <p style={{ fontFamily: '"Lato", sans-serif', color: 'rgba(255,255,255,0.9)', lineHeight: 1.8 }} className="text-base mb-8">
                The Magic Dress is available in regular and plus sizes. We believe every body deserves to feel outrageously gorgeous. Call or email us to find your perfect fit.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:7346468620" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap border-2 border-white transition-all duration-200 hover:-translate-y-0.5" style={{ color: '#fff', fontFamily: '"Lato", sans-serif', backgroundColor: 'transparent' }}>
                  <i className="ri-phone-line"></i> Call Us
                </a>
                <a href="mailto:earthwisdom80@gmail.com" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg font-bold uppercase tracking-wide whitespace-nowrap transition-all duration-200 hover:-translate-y-0.5" style={{ backgroundColor: '#fff', color: '#7A2E2E', fontFamily: '"Lato", sans-serif' }}>
                  <i className="ri-mail-line"></i> Email Us
                </a>
              </div>
            </div>
          </section>
        </RevealDiv>
      </main>
      <Footer />
    </div>
  );
}
